import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { authPostRequest } from "../../api/authClient";
import { useAuthMe } from "../../hooks/useAuthMe";
import { SenrysaLogo } from "../../Components/SenrysaLogo";

const MIN_PASSWORD_LEN = 8;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const me = useAuthMe({ redirectOn401: false });

  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (me.data && !me.isError) {
      navigate("/", { replace: true });
    }
  }, [me.data, me.isError, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error("Reset link is invalid or missing the token. Request a new link from forgot password.");
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LEN) {
      toast.error(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await authPostRequest("/member/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      toast.success("Password updated. You can sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as { message?: string })?.message;
      toast.error(typeof msg === "string" && msg.length > 0 ? msg : "Could not reset password. The link may have expired.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200/60 bg-white/70 backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/40">
        <div className="flex items-center gap-3">
          <SenrysaLogo className="p-1" />
        </div>
      </header>

      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-emerald-900/5 dark:shadow-slate-900/50 border border-slate-200/60 dark:border-slate-700/60 p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Set new password</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">Choose a strong password for your account.</p>
            </div>

            {!token ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                This page needs a valid reset token from your email.{" "}
                <Link to="/forgot-password" className="font-medium underline">
                  Request a new link
                </Link>
                .
              </div>
            ) : null}

            <form className="w-full space-y-4 mt-4" onSubmit={onSubmit}>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="reset-new" className="text-sm font-medium text-gray-700 dark:text-slate-300">
                    New password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowNew((s) => !s)}
                    className="text-xs text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 transition-colors"
                  >
                    {showNew ? "Hide" : "Show"}
                  </button>
                </div>
                <input
                  id="reset-new"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LEN}
                  autoComplete="new-password"
                  disabled={!token}
                  className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500 disabled:opacity-60"
                  placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="reset-confirm" className="text-sm font-medium text-gray-700 dark:text-slate-300">
                    Confirm password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowConfirm((s) => !s)}
                    className="text-xs text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 transition-colors"
                  >
                    {showConfirm ? "Hide" : "Show"}
                  </button>
                </div>
                <input
                  id="reset-confirm"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LEN}
                  autoComplete="new-password"
                  disabled={!token}
                  className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500 disabled:opacity-60"
                  placeholder="Re-enter password"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !token}
                className="w-full p-3 rounded-lg text-white font-medium transition-all duration-200 bg-emerald-600 hover:bg-emerald-700 dark:bg-blue-600 dark:hover:bg-blue-500 focus:ring-2 focus:ring-emerald-500 dark:focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-800 disabled:opacity-70"
              >
                {isLoading ? "Updating…" : "Update password"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-600 dark:text-slate-400 mt-6">
              <Link to="/login" className="text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 font-medium">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
